import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button, makeStyles } from "@material-ui/core";
import Card from "../../../../components/Card/Card.js";
import CardBody from "../../../../components/Card/CardBody.js";
import Pnl from "../../../../components/Pnl/Pnl.js";
import PnlSkeleton from "../../../../components/Pnl/PnlSkeleton.js";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  startAsyncPnlDownload,
  getDownloadProgress,
  downloadFile,
} from "../../viewServices/calls/apiCalls";
import { ScenarioSelector } from "../../sharedComponents/ScenarioSelector";

const financialImpactPnLDisplayStyle = (theme, clientColor) => ({
  pnlContainer: {
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    "@media (min-width: 960px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    overflow: "auto", // Enable scrolling for the container
  },
  pnlCard: {
    minHeight: "400px",
    maxHeight: "600px", // Set maximum height to enable scrolling
    position: "relative",
    marginTop: 0,
    display: "flex",
    flexDirection: "column",
  },
  pnlCardBody: {
    padding: 0,
    position: "relative",
    flex: 1, // Take remaining height in card
    minHeight: 0, // Allow flex item to shrink below content size
    overflow: "hidden", // Let PnL component handle scrolling internally
  },
  scenarioSelectorContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    zIndex: 3,
    backgroundColor: "rgba(255, 255, 255, 0.8)", // Match source
    padding: "12px 16px",
    borderRadius: "4px",
    width: "90%",
    maxWidth: 280,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)", // Match source
  },
  selectorLabel: {
    color: "#888", // Match source
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: theme.spacing(1.5),
  },
  selectFormControl: {
    width: "100%",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: "#999 !important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: "#666 !important", // Neutral color instead of primary
      },
    },
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.7)", // Slightly more transparent
    zIndex: 2,
  },
  errorState: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    zIndex: 2,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    width: "90%",
    maxWidth: 280,
  },
});

FinancialImpactPnLDisplay.propTypes = {
  selectedScenarios: PropTypes.array.isRequired,
  availableScenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  pnlData: PropTypes.object.isRequired,
  loadingStates: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  drillDownsOpen: PropTypes.object.isRequired,
  mouseOverPLLine: PropTypes.string,
  mouseOverPLLineFade: PropTypes.string,
  mouseOverVal: PropTypes.bool,
  clientColor: PropTypes.string.isRequired,
  onSelectScenario: PropTypes.func.isRequired,
  onRemoveScenario: PropTypes.func.isRequired,
  onRetryScenario: PropTypes.func.isRequired,
  createSetDrillDownsOpenForScenario: PropTypes.func.isRequired,
  handleMouseLeavePLLine: PropTypes.func.isRequired,
  handleMouseOverPLLine: PropTypes.func.isRequired,
  handleMouseLeavePLVal: PropTypes.func.isRequired,
  handleMouseOverPLVal: PropTypes.func.isRequired,
};

FinancialImpactPnLDisplay.displayName = "FinancialImpactPnLDisplay";

export default function FinancialImpactPnLDisplay({
  selectedScenarios,
  availableScenarios,
  folders = [],
  pnlData,
  loadingStates,
  errors,
  drillDownsOpen,
  mouseOverPLLine,
  mouseOverPLLineFade,
  mouseOverVal,
  clientColor,
  onSelectScenario,
  onRemoveScenario,
  onRetryScenario,
  createSetDrillDownsOpenForScenario,
  handleMouseLeavePLLine,
  handleMouseOverPLLine,
  handleMouseLeavePLVal,
  handleMouseOverPLVal,
}) {
  const useStyles = makeStyles((theme) =>
    financialImpactPnLDisplayStyle(theme, clientColor),
  );
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const handleDownload = async (scenario) => {
    try {
      const fullTable = false;
      const downloadJob = await startAsyncPnlDownload(
        scenario.scenario_id,
        fullTable,
      );
      const downloadId = downloadJob.download_id;
      const progressToastId = `download-progress-${downloadId}`;
      toast.info(
        <div>
          <div style={{ marginBottom: "8px" }}>
            {t("pnl.download.starting")}
          </div>
          <div
            style={{
              width: "100%",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
              height: "8px",
            }}
          >
            <div
              style={{
                width: "0%",
                backgroundColor: clientColor,
                height: "100%",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <div
            style={{
              fontSize: "0.8em",
              marginTop: "4px",
              color: "#666",
            }}
          >
            {t("pnl.download.progress", { progress: 0 })}
          </div>
        </div>,
        {
          toastId: progressToastId,
          autoClose: false,
          closeButton: false,
          position: "top-right",
        },
      );

      const pollInterval = setInterval(async () => {
        try {
          const progress = await getDownloadProgress(downloadId);
          toast.update(progressToastId, {
            render: (
              <div>
                <div style={{ marginBottom: "8px" }}>{progress.stage}</div>
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    height: "8px",
                  }}
                >
                  <div
                    style={{
                      width: `${progress.progress}%`,
                      backgroundColor: clientColor,
                      height: "100%",
                      borderRadius: "4px",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "0.8em",
                    marginTop: "4px",
                    color: "#666",
                  }}
                >
                  {t("pnl.download.progress", {
                    progress: Math.round(progress.progress),
                  })}
                </div>
              </div>
            ),
            type: "info",
            autoClose: false,
            closeButton: false,
          });

          if (progress.status === "success" && progress.file_ready) {
            clearInterval(pollInterval);
            toast.update(progressToastId, {
              render: (
                <div>
                  <div style={{ marginBottom: "8px" }}>
                    {t("pnl.download.ready")}
                  </div>
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "4px",
                      height: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#4caf50",
                        height: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "0.8em",
                      marginTop: "4px",
                      color: "#666",
                    }}
                  >
                    {t("pnl.download.complete")}
                  </div>
                </div>
              ),
              type: "success",
              autoClose: false,
              closeButton: true,
              closeOnClick: true,
            });

            try {
              const fileBlob = await downloadFile(downloadId);
              const url = URL.createObjectURL(fileBlob);
              const a = document.createElement("a");
              a.href = url;
              let filename = `pnl_scenario_${scenario.scenario_id}`;
              a.download = `${filename}.xlsx`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              // Success already indicated via the progress toast; no extra toast needed
            } catch (fileErr) {
              console.error("Error downloading P&L file:", fileErr);
              toast.error(t("pnl.download.file_failed"));
            }
          } else if (progress.status === "error") {
            clearInterval(pollInterval);
            toast.update(progressToastId, {
              render: (
                <div>
                  <div style={{ marginBottom: "8px" }}>
                    {t("pnl.download.failed", {
                      error: progress.error_message,
                    })}
                  </div>
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "4px",
                      height: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#f44336",
                        height: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "0.8em",
                      marginTop: "4px",
                      color: "#666",
                    }}
                  >
                    {t("pnl.download.error_occurred")}
                  </div>
                </div>
              ),
              type: "error",
              autoClose: false,
              closeButton: true,
              closeOnClick: true,
            });
          }
        } catch (pollError) {
          console.error("Error polling P&L download progress:", pollError);
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(pollInterval);
        toast.error(t("pnl.download.timeout"));
      }, 600000);
    } catch (err) {
      console.error("Failed to start P&L async download:", err);
      toast.error(t("pnl.download.start_failed"));
    }
  };

  return (
    <Box className={classes.pnlContainer}>
      {/* Sequential reveal: Show slots based on filled scenarios (1 → 2 → 3) */}
      {(() => {
        const filledCount = selectedScenarios.filter((s) => s !== null).length;
        const slotsToShow = Math.min(filledCount + 1, 3); // Always show one empty slot ahead
        return Array.from({ length: slotsToShow }, (_, i) => i);
      })().map((slotIndex) => {
        const scenario = selectedScenarios[slotIndex];
        const isPlaceholder = !scenario;

        return (
          <Card key={slotIndex} className={classes.pnlCard}>
            <CardBody className={classes.pnlCardBody}>
              {/* Base layer: Always show skeleton for placeholders or when loading/no data */}
              {(isPlaceholder ||
                (!pnlData[scenario?.scenario_id] &&
                  !errors[scenario?.scenario_id])) && <PnlSkeleton />}

              {/* Scenario selector overlay - show for empty slots */}
              {isPlaceholder && (
                <Box className={classes.scenarioSelectorContainer}>
                  <Typography className={classes.selectorLabel}>
                    {t("map.tooltip.select_scenario")}
                  </Typography>
                  <ScenarioSelector
                    scenarios={availableScenarios}
                    folders={folders}
                    value={null}
                    onChange={(scenarioId) => {
                      if (scenarioId) {
                        onSelectScenario(scenarioId);
                      }
                    }}
                    placeholder={t("pnl.choose_scenario_label")}
                    clientColor={clientColor}
                    fullWidth
                  />
                </Box>
              )}

              {/* Content layer: Show actual PnL when data is available */}
              {!isPlaceholder &&
                pnlData[scenario.scenario_id] &&
                !errors[scenario.scenario_id] &&
                (() => {
                  // Destructure to separate drillDownsOpen from other props
                  // eslint-disable-next-line no-unused-vars
                  const { drillDownsOpen: apiDrillDowns, ...pnlProps } =
                    pnlData[scenario.scenario_id];
                  return (
                    <Pnl
                      {...pnlProps}
                      // Drill-down state management
                      drillDownsOpen={
                        Array.isArray(drillDownsOpen[scenario.scenario_id])
                          ? drillDownsOpen[scenario.scenario_id]
                          : []
                      }
                      setDrillDownsOpen={createSetDrillDownsOpenForScenario(
                        scenario.scenario_id,
                      )}
                      // Basic required props
                      timersAllowed={true}
                      addPL={() => {}}
                      removePL={() => onRemoveScenario(scenario.scenario_id)}
                      // Mouse interaction props for line highlighting
                      mouseOverPLLine={mouseOverPLLine}
                      mouseOverPLLineFade={mouseOverPLLineFade}
                      mouseOverVal={mouseOverVal}
                      handleMouseLeavePLLine={handleMouseLeavePLLine}
                      handleMouseOverPLLine={handleMouseOverPLLine}
                      handleMouseLeavePLVal={handleMouseLeavePLVal}
                      handleMouseOverPLVal={handleMouseOverPLVal}
                      // Pin functionality
                      pinnedName={null}
                      setPinnedName={() => {}}
                      // Download handler
                      onDownload={() => handleDownload(scenario)}
                    />
                  );
                })()}

              {/* Error layer: Show error state when there's an error */}
              {!isPlaceholder && errors[scenario.scenario_id] && (
                <Box className={classes.errorState}>
                  <Typography color="error" variant="body2">
                    {errors[scenario.scenario_id]}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => onRetryScenario(scenario.scenario_id)}
                    color="primary"
                  >
                    {t("common.retry")}
                  </Button>
                </Box>
              )}

              {/* Overlay layer: Show loading spinner when loading */}
              {!isPlaceholder && loadingStates[scenario.scenario_id] && (
                <Box className={classes.loadingOverlay}>
                  <ThreeDots
                    height="60"
                    width="60"
                    radius="9"
                    color={clientColor}
                    ariaLabel="three-dots-loading"
                    visible={true}
                  />
                </Box>
              )}
            </CardBody>
          </Card>
        );
      })}
    </Box>
  );
}
